"use client";

import type { ComponentProps, MouseEvent } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { withCurrentReturnContext } from "@/lib/navigation/return-context";

type ContextualLinkProps = Omit<ComponentProps<typeof Link>, "href"> & {
  href: string;
};

function setContextualHref(element: HTMLAnchorElement, href: string) {
  element.setAttribute("href", withCurrentReturnContext(href));
}

function isModifiedOrNewTabClick(event: MouseEvent<HTMLAnchorElement>) {
  return (
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    event.currentTarget.target === "_blank"
  );
}

export function ContextualLink({
  href,
  onClick,
  onContextMenu,
  onFocus,
  onPointerDown,
  replace = false,
  scroll,
  ...props
}: ContextualLinkProps) {
  const router = useRouter();
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (event.defaultPrevented) return;
    const resolvedHref = withCurrentReturnContext(href);
    if (isModifiedOrNewTabClick(event)) {
      event.currentTarget.setAttribute("href", resolvedHref);
      return;
    }
    event.preventDefault();
    const navigate = replace ? router.replace : router.push;
    navigate(resolvedHref, { scroll });
  };

  return (
    <Link
      {...props}
      data-contextual-link=""
      href={href}
      replace={replace}
      scroll={scroll}
      onFocus={(event) => {
        setContextualHref(event.currentTarget, href);
        onFocus?.(event);
      }}
      onContextMenu={(event) => {
        setContextualHref(event.currentTarget, href);
        onContextMenu?.(event);
      }}
      onPointerDown={(event) => {
        setContextualHref(event.currentTarget, href);
        onPointerDown?.(event);
      }}
      onClick={handleClick}
    />
  );
}

export function contextualizeClosestAnchor(
  target: EventTarget | null,
): HTMLAnchorElement | null {
  if (!(target instanceof Element)) return null;
  const anchor = target.closest<HTMLAnchorElement>("a[href]");
  if (!anchor) return null;
  const href = anchor.getAttribute("href");
  if (!href) return null;
  const originalHref = anchor.dataset.returnContext ?? href;
  const contextualHref = withCurrentReturnContext(originalHref);
  if (contextualHref !== originalHref) {
    anchor.setAttribute("href", contextualHref);
    anchor.dataset.returnContext = originalHref;
    return anchor;
  }
  anchor.setAttribute("href", originalHref);
  delete anchor.dataset.returnContext;
  return null;
}
